import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MagicLinkService } from '../services/magicLinkService';
import { ElectionService, VoteService } from '../services/firebaseService';
import { Member, Election, Vote } from '../types';
import { 
  CheckCircleIcon, 
  ExclamationTriangleIcon,
  ClockIcon,
  UserIcon
} from '@heroicons/react/24/outline';

const MagicVotePage: React.FC = () => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const [member, setMember] = useState<Member | null>(null);
  const [elections, setElections] = useState<Election[]>([]);
  const [votes, setVotes] = useState<Vote[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const authenticateAndLoadData = async () => {
      if (!token) {
        setError('Token manquant');
        setLoading(false);
        return;
      }



      try {
        // Valider le lien magique
        const memberData = await MagicLinkService.validateAndUseMagicLink(token);
        setMember(memberData);
        setIsAuthenticated(true);
        if (!memberData) {
          setError('Membre non trouvé');
          setLoading(false);
          return;
        }
        // Charger les élections et votes
        const [electionsData, votesData] = await Promise.all([
          ElectionService.getActiveElections(),
          VoteService.getVotesByMember(memberData.id)
        ]);
        
        setElections(electionsData);
        setVotes(votesData);

      } catch (error: any) {
        console.error('Erreur d\'authentification:', error);
        setError(error.message || 'Erreur lors de l\'authentification');
      } finally {
        setLoading(false);
      }
    };

    authenticateAndLoadData();
  }, [token]);

  const hasVoted = (electionId: string) => {
    return votes.some(vote => vote.electionId === electionId);
  };

  const getVoteForElection = (electionId: string) => {
    return votes.find(vote => vote.electionId === electionId);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Authentification en cours...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md mx-auto">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-red-400 mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Erreur d'authentification</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <div className="space-y-2">
            <button
              onClick={() => navigate('/login')}
              className="btn-primary w-full"
            >
              Se connecter manuellement
            </button>
            <button
              onClick={() => navigate('/')}
              className="btn-secondary w-full"
            >
              Retour à l'accueil
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || !member) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header avec informations du membre */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="flex-shrink-0">
                <div className="h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                  <UserIcon className="h-6 w-6 text-primary-600" />
                </div>
              </div>
              <div>
                <h1 className="text-lg font-semibold text-gray-900">
                  Bonjour {member.firstName} {member.lastName}
                </h1>
                <p className="text-sm text-gray-500">
                  Connecté via lien magique • {member.email}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <ClockIcon className="h-5 w-5 text-green-500" />
              <span className="text-sm text-green-600 font-medium">
                Session active
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900">Élections ASPROCOM</h2>
          <p className="mt-2 text-gray-600">
            Vous êtes connecté via un lien sécurisé. Vos votes sont anonymes et sécurisés.
          </p>
        </div>

        {elections.length === 0 ? (
          <div className="text-center py-12">
            <CheckCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune élection active</h3>
            <p className="mt-1 text-sm text-gray-500">
              Il n'y a actuellement aucune élection en cours.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {elections.map((election) => {
              const hasVotedForThis = hasVoted(election.id);
              const userVote = getVoteForElection(election.id);

              return (
                <div key={election.id} className="card">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold text-gray-900">
                      {election.title}
                    </h3>
                    {hasVotedForThis && (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <CheckCircleIcon className="w-4 h-4 mr-1" />
                        Voté
                      </span>
                    )}
                  </div>

                  <p className="text-gray-600 mb-4">{election.description}</p>

                  <div className="space-y-3">
                    {election.candidates.map((candidate) => (
                      <div
                        key={candidate.id}
                        className={`p-4 border rounded-lg ${
                          hasVotedForThis && userVote?.candidateId === candidate.id
                            ? 'border-green-500 bg-green-50'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <h4 className="font-medium text-gray-900">
                              {candidate.name}
                            </h4>
                            {candidate.bio && (
                              <p className="text-sm text-gray-600 mt-1">
                                {candidate.bio}
                              </p>
                            )}
                          </div>
                          {hasVotedForThis && userVote?.candidateId === candidate.id && (
                            <CheckCircleIcon className="w-5 h-5 text-green-500" />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <p className="text-sm text-gray-500">
                      Période de vote : {election.startDate.toLocaleDateString()} - {election.endDate.toLocaleDateString()}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {votes.length > 0 && (
          <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <h3 className="font-medium text-blue-900 mb-2">Vos votes</h3>
            <p className="text-sm text-blue-700">
              Vous avez voté pour {votes.length} élection(s). Vos choix sont enregistrés de manière sécurisée.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default MagicVotePage;
